import React, { useState, useEffect } from 'react';
import { Search, Filter, ChevronDown, Loader2 } from 'lucide-react';
import axios from 'axios';
import { API } from '@/api';
import { useLang } from '../../contexts/LanguageContext';


// Opciones de respaldo fijas (cuando la BD local no tiene cartas sincronizadas)
const RARITY_FALLBACK = {
  pokemon:   ['Common', 'Uncommon', 'Rare', 'Rare Holo', 'Rare Holo EX', 'Rare Holo GX', 'Rare Ultra', 'Rare Secret', 'Hyper Rare'],
  magic:     ['common', 'uncommon', 'rare', 'mythic'],
  yugioh:    ['Common', 'Rare', 'Super Rare', 'Ultra Rare', 'Secret Rare'],
  onepiece:  ['C', 'UC', 'R', 'SR', 'L'],
  riftbound: ['common', 'uncommon', 'rare', 'epic', 'showcase'],
};

const TYPE_FALLBACK = {
  pokemon:   ['Fire', 'Water', 'Grass', 'Electric', 'Psychic', 'Fighting', 'Dark', 'Dragon', 'Fairy', 'Metal', 'Colorless'],
  magic:     ['Creature', 'Instant', 'Sorcery', 'Enchantment', 'Artifact', 'Planeswalker', 'Land'],
  yugioh:    ['Effect Monster', 'Normal Monster', 'Fusion Monster', 'Synchro Monster', 'XYZ Monster', 'Link Monster', 'Spell Card', 'Trap Card'],
  onepiece:  ['Leader', 'Character', 'Event', 'Stage'],
  riftbound: ['Unit', 'Spell', 'Gear', 'Battlefield'],
};

export default function SearchFilters({
  lang,
  selectedTcg,
  searchQuery,
  setSearchQuery,
  onSearch,
  rarityFilter,
  setRarityFilter,
  typeFilter,
  setTypeFilter,
  setFilter,
  setSetFilter,
  availableSets,
  setsLoading,
  showFilters,
  setShowFilters,
  onClearFilters,
  onPageReset
}) {
  const [dynamicRarities, setDynamicRarities] = useState([]);
  const [dynamicTypes, setDynamicTypes] = useState([]);
  const [filtersFromDb, setFiltersFromDb] = useState(false);
  const { t } = useLang();

  // Cargar opciones de filtro dinámicas al cambiar el TCG
  useEffect(() => {
    if (!selectedTcg) return;
    axios.get(`${API}/tcg/catalog/filters`, { params: { tcg_type: selectedTcg } })
      .then(({ data }) => {
        if (data.from_db && (data.rarities.length > 0 || data.types.length > 0)) {
          setDynamicRarities(data.rarities);
          setDynamicTypes(data.types);
          setFiltersFromDb(true);
        } else {
          setDynamicRarities([]);
          setDynamicTypes([]);
          setFiltersFromDb(false);
        }
      })
      .catch(() => {
        setDynamicRarities([]);
        setDynamicTypes([]);
        setFiltersFromDb(false);
      });
  }, [selectedTcg]);

  // Usar opciones dinámicas de BD si hay; si no, respaldo fijo
  const rarityOptions = filtersFromDb ? dynamicRarities : (RARITY_FALLBACK[selectedTcg] || []);
  const typeOptions = filtersFromDb ? dynamicTypes : (TYPE_FALLBACK[selectedTcg] || []);

  const hasActiveFilters = rarityFilter || typeFilter || setFilter;
  const activeFilterCount = [rarityFilter, typeFilter, setFilter].filter(Boolean).length;

  return (
    <form onSubmit={onSearch} className="mb-8">
      <div className="relative max-w-xl">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder={t('catalog.search_cards_placeholder')}
          className="w-full pl-12 pr-4 py-3 bg-[#12121A] border border-white/10 rounded-xl text-white text-sm outline-none focus:border-[#10B981]"
          data-testid="card-search-input"
        />
        <button 
          type="submit" 
          className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-1.5 bg-[#10B981] text-[#0A0A0E] rounded-lg text-sm font-semibold hover:bg-[#34D399] transition-colors"
          data-testid="card-search-btn"
        >
          {t('catalog.search')}
        </button>
      </div>
      
      {/* Filter Toggle Button */}
      <div className="flex items-center gap-3 mt-3">
        <button
          type="button"
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            showFilters || hasActiveFilters
              ? 'bg-[#10B981]/20 text-[#10B981] border border-[#10B981]/30'
              : 'bg-[#12121A] text-[#A1A1AA] border border-white/10 hover:border-white/20'
          }`}
          data-testid="filter-toggle-btn"
        >
          <Filter size={16} />
          {t('catalog.filters')}
          {activeFilterCount > 0 && (
            <span className="bg-[#10B981] text-[#0A0A0E] text-xs px-1.5 py-0.5 rounded-full">
              {activeFilterCount}
            </span>
          )}
          <ChevronDown size={14} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} />
        </button>
        
        {hasActiveFilters && (
          <button
            type="button"
            onClick={onClearFilters}
            className="text-[#A1A1AA] text-sm hover:text-white transition-colors"
            data-testid="clear-filters-btn"
          >
            {t('catalog.clear_filters')}
          </button>
        )}
      </div>
      
      {/* Filter Dropdowns */}
      {showFilters && (
        <div className="flex flex-wrap gap-3 mt-3 p-4 bg-[#12121A] border border-white/10 rounded-xl">
          {/* Set/Expansion Filter */}
          <div className="flex-1 min-w-[220px]">
            <label className="block text-[#A1A1AA] text-xs mb-1.5 flex items-center gap-2">
              {t('catalog.set_expansion')}
              {setsLoading && <Loader2 size={12} className="animate-spin" />}
            </label>
            <select
              value={setFilter}
              onChange={(e) => { setSetFilter(e.target.value); onPageReset(); }}
              className="w-full px-3 py-2 bg-[#0A0A0E] border border-white/10 rounded-lg text-white text-sm outline-none focus:border-[#10B981]"
              data-testid="set-filter"
              disabled={setsLoading}
            >
              <option value="">{t('catalog.all_sets')} ({availableSets.length})</option>
              {availableSets.map(s => (
                <option key={s.set_id} value={s.set_id}>
                  {s.name} {s.total_cards ? `(${s.total_cards})` : ''}
                </option>
              ))}
            </select>
          </div>
          
          {/* Rarity Filter */}
          <div className="flex-1 min-w-[180px]">
            <label className="block text-[#A1A1AA] text-xs mb-1.5 flex items-center gap-1.5">
              {t('catalog.rarity')}
              {filtersFromDb && <span className="text-[#2D8B70] text-[9px]">● DB</span>}
            </label>
            <select
              value={rarityFilter}
              onChange={(e) => { setRarityFilter(e.target.value); onPageReset(); }}
              className="w-full px-3 py-2 bg-[#0A0A0E] border border-white/10 rounded-lg text-white text-sm outline-none focus:border-[#10B981]"
              data-testid="rarity-filter"
            >
              <option value="">{t('catalog.all_rarities')}</option>
              {rarityOptions.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
          
          {/* Type Filter */}
          <div className="flex-1 min-w-[180px]">
            <label className="block text-[#A1A1AA] text-xs mb-1.5 flex items-center gap-1.5">
              {t('catalog.type')}
              {filtersFromDb && <span className="text-[#2D8B70] text-[9px]">● DB</span>}
            </label>
            <select
              value={typeFilter}
              onChange={(e) => { setTypeFilter(e.target.value); onPageReset(); }}
              className="w-full px-3 py-2 bg-[#0A0A0E] border border-white/10 rounded-lg text-white text-sm outline-none focus:border-[#10B981]"
              data-testid="type-filter"
            >
              <option value="">{t('catalog.all_types')}</option>
              {typeOptions.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
        </div>
      )}
    </form>
  );
}
